import React from "react";
import { useCallback, useMemo, useState } from "react";

import { getIn, useFormikContext } from "formik";
import * as Yup from "yup";

import { DialogBody } from "../components";
import { DialogStepperButtons } from "../components/DialogStepperButtons";

export interface FormSections {
  formKey: string;
  include: boolean;
  schema: Yup.ObjectSchema<any>;
}

interface DialogStepperProps {
  formSections: FormSections[];
  renderSectionComponent: (formKey: string) => JSX.Element | null;
}

function getAllSchemaKeys(schema: Yup.ObjectSchema<any>, prefix = ""): string[] {
  const keys: string[] = [];

  for (const key in schema.fields) {
    const field = schema.fields[key];
    const path = prefix ? `${prefix}.${key}` : key;

    if (field instanceof Yup.ObjectSchema) {
      keys.push(...getAllSchemaKeys(field, path));
    } else if (field instanceof Yup.ArraySchema) {
      keys.push(path);
      if (field.innerType instanceof Yup.ObjectSchema) {
        keys.push(...getAllSchemaKeys(field.innerType, `${path}[]`));
      }
    } else {
      keys.push(path);
    }
  }

  return keys;
}

const DialogStepper = ({ formSections, renderSectionComponent }: DialogStepperProps) => {
  const { setFieldTouched, values } = useFormikContext();

  const includedSections = useMemo(() => formSections.filter(section => section.include), [formSections]);
  const [showKey, setShowKey] = useState<string>(includedSections[0]?.formKey);

  const handleNext = useCallback(async () => {
    const currentSection = formSections.find(section => section.formKey === showKey);
    const currentSchema = currentSection?.schema;

    if (!currentSchema) {
      return;
    }

    const allKeys = getAllSchemaKeys(currentSchema);
    allKeys.forEach(key => {
      setFieldTouched(key, true, true);
    });

    let hasErrors = false;
    for (const key of allKeys) {
      try {
        await currentSchema.validateAt(key, values);
      } catch (err) {
        console.error(`Validation error for ${key}:`, err);
        hasErrors = true;
      }
    }

    if (hasErrors) {
      return;
    }

    const currentIndex = formSections.findIndex(section => section.formKey === showKey);
    const nextIncludedSection = formSections.slice(currentIndex + 1).find(section => section.include);

    if (nextIncludedSection) {
      setShowKey(nextIncludedSection.formKey);
    }
  }, [formSections, showKey, , setFieldTouched, values, setShowKey]);

  const handleBack = useCallback(() => {
    const currentIndex = formSections.findIndex(section => section.formKey === showKey);
    const previousIncludedSection = [...formSections.slice(0, currentIndex)].reverse().find(section => section.include);

    if (previousIncludedSection) {
      setShowKey(previousIncludedSection.formKey);
    }
  }, [formSections, showKey]);

  const isFirst = useMemo(() => {
    return showKey === includedSections[0]?.formKey;
  }, [showKey, includedSections]);

  const isLast = useMemo(() => {
    return showKey === includedSections.slice(-1)[0]?.formKey;
  }, [showKey, includedSections]);

  return (
    <>
      <DialogBody>
        {includedSections
          .filter(section => section.formKey === showKey)
          .map(section => (
            <React.Fragment key={section.formKey}> {renderSectionComponent(section.formKey)}</React.Fragment>
          ))}
      </DialogBody>
      <DialogStepperButtons onNext={handleNext} onBack={handleBack} isFirstStep={isFirst} isLastStep={isLast} />
    </>
  );
};

export default DialogStepper;
